<template>
  <div class="sales-funnel">
    <!-- Skeleton -->
    <div v-if="loading" class="funnel-skeleton">
      <div v-for="n in 4" :key="n" class="skeleton-bar-row">
        <div class="skeleton-label"></div>
        <div class="skeleton-bar" :style="{ width: 100 - n * 15 + '%' }"></div>
        <div class="skeleton-value"></div>
      </div>
    </div>

    <!-- Funnel bars -->
    <div v-else class="funnel-body">
      <div v-for="stage in stages" :key="stage.key" class="funnel-row">
        <span class="funnel-label">{{ stage.label }}</span>
        <div class="funnel-bar-track">
          <div
            class="funnel-bar"
            :style="{
              width: animated ? stage.percentage + '%' : '0%',
              background: stage.color
            }"
          ></div>
        </div>
        <span class="funnel-count">{{ stage.count }}</span>
        <span class="funnel-pct">{{ stage.percentage }}%</span>
      </div>
      <div class="funnel-footer" v-if="avgCloseDays !== null">
        <i class="el-icon-timer"></i>
        Tiempo promedio de cierre: <strong>{{ avgCloseDays }} días</strong>
      </div>
      <div class="funnel-footer funnel-footer-muted" v-else-if="!loading">
        <i class="el-icon-timer"></i>
        Cierre promedio: <span>Sin datos suficientes</span>
      </div>
    </div>
  </div>
</template>

<script>
import dashboardService from "../services/dashboardService";

export default {
  name: "SalesFunnel",
  props: {
    period: { type: String, default: "month" }
  },
  data() {
    return {
      loading: true,
      animated: false,
      cache: {},
      avgCloseDays: null,
      stages: [
        {
          key: "quoted",
          label: "Cotizado",
          count: 0,
          percentage: 0,
          color: "#31355c"
        },
        {
          key: "in_progress",
          label: "En progreso",
          count: 0,
          percentage: 0,
          color: "#534ab7"
        },
        {
          key: "approved",
          label: "Aprobado",
          count: 0,
          percentage: 0,
          color: "#3fdbbf"
        },
        {
          key: "invoiced",
          label: "Facturado",
          count: 0,
          percentage: 0,
          color: "#1d9e75"
        }
      ]
    };
  },
  watch: {
    period: {
      immediate: true,
      handler(val) {
        this.fetchFunnel(val);
      }
    }
  },
  methods: {
    async fetchFunnel(period) {
      if (this.cache[period]) {
        this.applyData(this.cache[period]);
        return;
      }

      this.loading = true;
      this.animated = false;
      try {
        const res = await dashboardService.getFunnel({
          params: { period }
        });
        if (res.data && res.data.success) {
          const data = res.data.data;
          this.cache[period] = data;
          this.applyData(data);
        }
      } catch (e) {
        // Mantener valores actuales
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          setTimeout(() => {
            this.animated = true;
          }, 50);
        });
      }
    },
    applyData(data) {
      const funnel = data.funnel || data;
      this.stages.forEach(stage => {
        if (funnel[stage.key]) {
          stage.count = funnel[stage.key].count || 0;
          stage.percentage = funnel[stage.key].percentage || 0;
        }
      });
      this.avgCloseDays = data.avg_close_days || null;
    }
  }
};
</script>

<style lang="scss" scoped>
$primary: #31355c;
$accent: #3fdbbf;
$border: #e4eaf6;
$bg: #f7f8f9;

.sales-funnel {
  background: #fff;
  border: 1px solid $border;
  border-radius: 10px;
  padding: 18px;
}
.funnel-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.funnel-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.funnel-label {
  width: 90px;
  font-size: 12px;
  font-weight: 600;
  color: #606266;
  flex-shrink: 0;
}
.funnel-bar-track {
  flex: 1;
  height: 22px;
  background: $bg;
  border-radius: 6px;
  overflow: hidden;
}
.funnel-bar {
  height: 100%;
  border-radius: 6px;
  transition: width 0.8s cubic-bezier(0.25, 0.8, 0.25, 1);
  min-width: 2px;
}
.funnel-count {
  font-size: 13px;
  font-weight: 700;
  color: $primary;
  width: 32px;
  text-align: right;
}
.funnel-pct {
  font-size: 12px;
  color: #909399;
  width: 36px;
  text-align: right;
}
.funnel-footer {
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid $border;
  font-size: 12px;
  color: #909399;
  display: flex;
  align-items: center;
  gap: 6px;
  i {
    color: #ba7517;
  }
  strong {
    color: $primary;
  }
  &.funnel-footer-muted span {
    color: #c0c4cc;
    font-style: italic;
  }
}

// Skeleton
.funnel-skeleton {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-top: 4px;
}
.skeleton-bar-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.skeleton-label {
  width: 90px;
  height: 10px;
  background: linear-gradient(90deg, #f0f2f7 25%, #e8ecf5 50%, #f0f2f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  flex-shrink: 0;
}
.skeleton-bar {
  height: 22px;
  background: linear-gradient(90deg, #f0f2f7 25%, #e8ecf5 50%, #f0f2f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 6px;
}
.skeleton-value {
  width: 32px;
  height: 10px;
  background: linear-gradient(90deg, #f0f2f7 25%, #e8ecf5 50%, #f0f2f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 480px) {
  .funnel-label {
    width: 70px;
    font-size: 11px;
  }
}
</style>
